/**
 * The SOP library (plan docs/plans/lms-plan.md 11.1, section 13 tab 4; WP-46):
 * every SOP of the network with its number, title, category, owner, the
 * effective version and its date, and the training status: for an lms admin
 * "sent to n, acknowledged m, pending k" over the people the admin reaches
 * (the two counts can overlap, 18.27), for everyone their own training on it
 * (my_training, 18.25). Each row opens the effective file (presigned, inline,
 * a new tab, D15) and links to the SOP's page (?sop=<id>).
 *
 * GET /sops is read to its last page (200 a page; a network's library is
 * small) and searched here by number and title. An admin filters by status
 * (active by default, retired, or every SOP); a learner sees active SOPs only
 * (the server's rule).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Loader2 } from 'lucide-react';
import EmptyState from '../../../ui/EmptyState';
import SearchInput from '../../../ui/SearchInput';
import { chipCls, secondaryBtnCls } from '../../../ui/styles';
import { useLms } from '../../LmsProvider';
import { apiErrorMessage } from '../../api/client';
import { getSopVersionUrl, listAllSops } from '../../api/sops';
import type { SopListItem, SopStatus } from '../../api/sops';
import { Pill } from '../../components/StatusPill';
import {
  ErrorNote,
  formatDay,
  linkBtnCls,
  metaCls,
  rowCls,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  thCls,
  theadCls,
} from '../../components/pickers/shared';
import { formatSopDay, sopHref, SOP_STATUS_LABELS } from './labels';
import { OpenFileButton } from './OpenFileButton';

export interface SopListProps {
  isAdmin: boolean;
  onOpenSop: (sopId: string) => void;
}

type StatusFilter = SopStatus | 'all';

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'retired', label: 'Retired' },
  { value: 'all', label: 'All' },
];

/** Whether an SOP matches the search box (number or title, any case). */
export function matchesSop(item: Pick<SopListItem, 'sop_number' | 'title'>, q: string): boolean {
  const term = q.trim().toLowerCase();
  if (!term) return true;
  return item.sop_number.toLowerCase().includes(term) || item.title.toLowerCase().includes(term);
}

/** The caller's own training on the effective version, in words (18.25 my_training). */
function MyTrainingCell({ item, now }: { item: SopListItem; now: number }) {
  const my = item.my_training;
  if (my?.assignment) {
    const overdue = Date.parse(my.assignment.due_at) < now;
    return (
      <div className="flex flex-wrap items-center gap-2">
        {overdue ? (
          <Pill tone="red" data-testid="lms-sop-my-overdue">Overdue</Pill>
        ) : (
          <Pill tone="primary" data-testid="lms-sop-my-due">{`Due ${formatDay(my.assignment.due_at)}`}</Pill>
        )}
        {item.course_id && (
          <Link
            to={`/lms/courses/${encodeURIComponent(item.course_id)}/learn`}
            className={linkBtnCls}
            data-testid="lms-sop-start"
          >
            Start the training
          </Link>
        )}
      </div>
    );
  }
  if (my?.acknowledged_at) {
    return <Pill tone="green" data-testid="lms-sop-my-done">{`Acknowledged ${formatDay(my.acknowledged_at)}`}</Pill>;
  }
  return <span className={metaCls}>Nothing assigned</span>;
}

/** 11.1: "sent to n, acknowledged m, pending k" (admins). The two lists can overlap (18.27). */
function TrainingCell({ item }: { item: SopListItem }) {
  const t = item.training;
  if (!t) return <span className={metaCls}>Not sent for training</span>;
  return (
    <div className="space-y-0.5" data-testid="lms-sop-training">
      <div className="text-sm text-gray-900 dark:text-gray-100">
        {t.sent_to === null ? 'Sent' : `Sent to ${t.sent_to.toLocaleString()}`}
      </div>
      <div className={metaCls}>{`${t.acknowledged.toLocaleString()} acknowledged \u00b7 ${t.pending.toLocaleString()} pending`}</div>
    </div>
  );
}

export function SopList({ isAdmin, onOpenSop }: SopListProps) {
  const { siteId } = useLms();
  const [status, setStatus] = useState<StatusFilter>('active');
  const [q, setQ] = useState('');
  const [items, setItems] = useState<SopListItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const request = useRef(0);

  useEffect(() => {
    const id = ++request.current;
    setLoading(true);
    setError(null);
    const query = isAdmin ? { status: status === 'all' ? null : status } : {};
    listAllSops(query)
      .then((rows) => {
        if (id !== request.current) return;
        setItems(rows);
      })
      .catch((err) => {
        if (id !== request.current) return;
        setItems(null);
        setError(apiErrorMessage(err, 'The SOP library could not be loaded.'));
      })
      .finally(() => {
        if (id === request.current) setLoading(false);
      });
  }, [isAdmin, status, siteId, attempt]);

  const visible = useMemo(() => (items ?? []).filter((i) => matchesSop(i, q)), [items, q]);
  const now = Date.now();

  return (
    <div className="space-y-3" data-testid="lms-sop-library">
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput value={q} onChange={setQ} placeholder="Search by number or title" label="Search SOPs" className="w-full sm:w-72" />
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="SOP status">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                className={chipCls(status === f.value)}
                aria-pressed={status === f.value}
                onClick={() => setStatus(f.value)}
                data-testid={`lms-sop-filter-${f.value}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
        {loading && items && <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-label="Loading" />}
      </div>

      {error && (
        <ErrorNote testId="lms-sop-list-error">
          {error}{' '}
          <button type="button" className="font-medium underline" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </ErrorNote>
      )}

      {loading && !items && !error && (
        <div className="flex justify-center py-10" data-testid="lms-sop-list-loading">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          <span className="sr-only">Loading the SOP library</span>
        </div>
      )}

      {items && items.length === 0 && (
        <EmptyState
          icon={FileText}
          variant="card"
          title={isAdmin && status !== 'active' ? 'No SOPs match this filter' : 'No SOPs yet'}
          hint={
            isAdmin
              ? "Create an SOP with New SOP, then upload its first version and make it effective."
              : "Your site's standard operating procedures will be listed here once they are added."
          }
        />
      )}

      {items && items.length > 0 && visible.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="lms-sop-no-match">
          No SOP matches your search.
        </p>
      )}

      {visible.length > 0 && (
        <div className={tableCardCls}>
          <table className={tableCls} data-testid="lms-sop-table">
            <thead className={theadCls}>
              <tr>
                <th scope="col" className={thCls}>SOP</th>
                <th scope="col" className={`${thCls} hidden md:table-cell`}>Category</th>
                <th scope="col" className={`${thCls} hidden lg:table-cell`}>Owner</th>
                <th scope="col" className={thCls}>Effective version</th>
                <th scope="col" className={thCls}>{isAdmin ? 'Training' : 'Your training'}</th>
                <th scope="col" className={thCls}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className={tbodyCls}>
              {visible.map((item) => {
                const ev = item.effective_version;
                return (
                  <tr key={item.id} className={rowCls} data-testid="lms-sop-row" data-sop-id={item.id}>
                    <td className={tdCls}>
                      <Link
                        to={sopHref(item.id)}
                        className="font-medium text-gray-900 hover:text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100 dark:hover:text-primary-400"
                        data-testid="lms-sop-link"
                      >
                        <span className="tabular-nums">{item.sop_number}</span>
                        {' \u00b7 '}
                        {item.title}
                      </Link>
                      {item.status === 'retired' && (
                        <span className="ml-2">
                          <Pill tone="gray">{SOP_STATUS_LABELS.retired}</Pill>
                        </span>
                      )}
                      <div className={`${metaCls} md:hidden`}>{item.category || ''}</div>
                    </td>
                    <td className={`${tdCls} hidden md:table-cell`}>{item.category || <span className={metaCls}>{'\u2014'}</span>}</td>
                    <td className={`${tdCls} hidden lg:table-cell`}>{item.owner?.full_name || <span className={metaCls}>{'\u2014'}</span>}</td>
                    <td className={tdCls}>
                      {ev ? (
                        <div>
                          <div className="font-medium tabular-nums">{`Version ${ev.version_label}`}</div>
                          <div className={metaCls}>{`Effective ${formatSopDay(ev.effective_on || ev.made_effective_at)}`}</div>
                        </div>
                      ) : (
                        <span className={metaCls}>No effective version</span>
                      )}
                    </td>
                    <td className={tdCls}>{isAdmin ? <TrainingCell item={item} /> : <MyTrainingCell item={item} now={now} />}</td>
                    <td className={`${tdCls} text-right`}>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {ev && (
                          <OpenFileButton
                            resolve={() => getSopVersionUrl(item.id, ev.id)}
                            label="Open"
                            ariaLabel={`Open SOP ${item.sop_number}, version ${ev.version_label}`}
                            testId="lms-sop-open"
                            failureMessage="The SOP file could not be opened. Try again."
                          />
                        )}
                        {isAdmin && (
                          <button type="button" className={secondaryBtnCls} onClick={() => onOpenSop(item.id)} data-testid="lms-sop-manage">
                            Manage
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SopList;
